import { useMemo, useState } from "react";
import EditModal from "../ui/EditModal";
import { formatDate } from "../../utils/dateUtils";

function getEditFields(type) {
  const dateField = { name: "date", label: "Date & Time", type: "datetime-local", required: true };
  const notesField = { name: "notes", label: "Notes", type: "textarea", rows: 2 };

  switch (type) {
    case "growth":
      return [
        dateField,
        { name: "weight", label: "Weight (lbs)", type: "number", step: "0.1", min: "0" },
        { name: "height", label: "Height (inches)", type: "number", step: "0.1", min: "0" },
        { name: "headCircumference", label: "Head Circumference (inches)", type: "number", step: "0.1", min: "0" },
        notesField,
      ];
    case "temperature":
      return [
        dateField,
        { name: "temperature", label: "Temperature (°F)", type: "number", step: "0.1", min: "0", required: true },
        notesField,
      ];
    case "medication":
      return [
        dateField,
        { name: "medication", label: "Medication", type: "text", required: true },
        { name: "dosage", label: "Dosage", type: "text", required: true },
        { name: "reason", label: "Reason", type: "text" },
        notesField,
      ];
    case "illness":
      return [
        dateField,
        { name: "symptoms", label: "Symptoms", type: "text", required: true },
        { name: "severity", label: "Severity", type: "select", options: [
          { label: "Mild", value: "mild" },
          { label: "Moderate", value: "moderate" },
          { label: "Severe", value: "severe" },
        ]},
        notesField,
      ];
    default:
      return [dateField, notesField];
  }
}

const HealthTable = ({ data, onUpdate, onDelete }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortDir, setSortDir] = useState("desc");
  const [filterType, setFilterType] = useState("all");
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const filteredData = useMemo(() => {
    if (filterType === "all") return data;
    return data.filter(item => item.type === filterType);
  }, [data, filterType]);

  const sorted = useMemo(() => {
    return filteredData.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortDir]);

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData = sorted.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const toggleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      await onUpdate(editingItem.id, formData);
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (err) {
      console.error("Error saving:", err);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        await onDelete(id);
      } catch (err) {
        console.error("Error deleting:", err);
        alert("Failed to delete entry");
      }
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      growth: "badge bg-success",
      temperature: "badge bg-warning text-dark",
      medication: "badge bg-info",
      illness: "badge bg-danger"
    };
    return badges[type] || "badge bg-secondary";
  };

  const renderData = (item) => {
    switch (item.type) {
      case "growth":
        return `${item.weight ? `${item.weight} lbs` : ""} ${item.height ? `${item.height}"` : ""} ${item.headCircumference ? `HC: ${item.headCircumference}"` : ""}`.trim();
      case "temperature":
        return `${item.temperature}°F`;
      case "medication":
        return `${item.medication} - ${item.dosage}`;
      case "illness":
        return item.symptoms;
      default:
        return "";
    }
  };

  const renderSeverityBadge = (severity) => {
    const colors = {
      mild: "badge bg-success",
      moderate: "badge bg-warning text-dark",
      severe: "badge bg-danger"
    };
    return colors[severity] || "";
  };

  return (
    <>
      <div className="mb-3">
        <select
          className="form-select form-select-sm"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{ width: "200px" }}
        >
          <option value="all">All Health Records</option>
          <option value="growth">Growth Measurements</option>
          <option value="temperature">Temperature</option>
          <option value="medication">Medications</option>
          <option value="illness">Illness/Symptoms</option>
        </select>
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col" style={{ cursor: "pointer" }} onClick={toggleSort}>
                DateTime {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th scope="col">Type</th>
              <th scope="col">Details</th>
              <th scope="col">Notes</th>
              <th scope="col" style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, index) => (
              <tr key={index}>
                <td>{formatDate(row.date)}</td>
                <td>
                  <span className={`badge ${getTypeBadge(row.type)}`}>
                    {row.type}
                  </span>
                  {row.severity && (
                    <span className={`badge ${renderSeverityBadge(row.severity)} ms-1`}>
                      {row.severity}
                    </span>
                  )}
                </td>
                <td>{renderData(row)}</td>
                <td>{row.notes || row.reason || <span className="text-muted">-</span>}</td>
                <td>
                  <button
                    className="btn btn-xs btn-warning me-1"
                    title="Edit"
                    onClick={() => handleEdit(row)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✎
                  </button>
                  <button
                    className="btn btn-xs btn-danger"
                    title="Delete"
                    onClick={() => handleDelete(row.id)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav aria-label="Page navigation">
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(pageCount)].map((_, idx) => {
              const num = idx + 1;
              return (
                <li key={num} className={`page-item ${num === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>
                    {num}
                  </button>
                </li>
              );
            })}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.min(prev + 1, pageCount))}
                disabled={page === pageCount}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      <EditModal
        isOpen={isModalOpen}
        item={editingItem}
        title={`Edit ${editingItem?.type ? editingItem.type.charAt(0).toUpperCase() + editingItem.type.slice(1) : "Health"} Record`}
        fields={editingItem ? getEditFields(editingItem.type) : []}
        onSave={handleSave}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        isLoading={isSaving}
      />
    </>
  );
};

export default HealthTable;
